import {
  useReducer,
  createContext,
  useRef,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import noop from 'lodash/noop';

import {
  getAudioFromPlaylist,
  getNextAudio,
  getPreviousAudio,
  PlaylistStateReducer,
} from './pop-out-audio.helpers';
import type { AudioItem, Playlist, PopOutAudioContextValue } from './types';
import { PlayStates, VisibilityStates } from '../pop-out-media-manager/types';
import { bowser } from '../../../global/browser-support';
import { VideoJSPlayer } from 'video.js';

export const PopOutAudioContext = createContext<PopOutAudioContextValue>({
  getCurrentlyActive: () => null,
  pauseAudio: noop,
  playAudio: noop,
  playNextAudio: noop,
  playPreviousAudio: noop,
  toggleAudioState: noop,
  addAudio: noop,
  setAllInactive: noop,
  setAudioPlayerInstance: noop,
  setAudioPlayerContainerHeight: noop,
  audioPlayerContainerHeight: 0,
  audioPlayerPortalOverrideParent: null,
  setAudioPlayerPortalOverrideParent: noop,
  audioPlayerPortalOverrideTrigger: false,
  setAudioPlayerPortalOverrideTrigger: noop,
  modalDialogClosedLatch: false,
  setModalDialogClosedLatch: noop,
  browserPausesAudioOnCloseModal: false,
  clearAudios: noop,
  playlist: [],
  popOutVisibilityState: VisibilityStates.Hidden,
});

type PopOutAudioProviderProps = {
  children: ReactNode;
  isAuthorisedToPlayAudio: boolean;
};

export const PopOutAudioProvider = ({
  children,
  isAuthorisedToPlayAudio,
}: PopOutAudioProviderProps) => {
  const [popOutState, dispatchPopOutStateAction] = useReducer(
    PlaylistStateReducer,
    {
      playlist: [],
      visibilityState: VisibilityStates.Hidden,
    },
  );
  const audioPlayerInstance = useRef<VideoJSPlayer | null>(null);
  const [audioPlayerContainerHeight, setAudioPlayerContainerHeight] =
    useState<number>(0);

  const [audioPlayerPortalOverrideParent, setAudioPlayerPortalOverrideParent] =
    useState<HTMLElement | null>(null);

  const [
    audioPlayerPortalOverrideTrigger,
    setAudioPlayerPortalOverrideTrigger,
  ] = useState<boolean>(false);

  // Some browsers pause audio when a modal dialog closes
  // possibly because of policies regarding hidden audio elements
  // and the way the modal fades out / hides itself

  // identify that the browser is one of those that will pause audio
  let browserPausesAudioOnCloseModal = false;
  const browsersThatPauseAudio = ['safari', 'firefox'];

  if (browsersThatPauseAudio.find(browserName => !!bowser[browserName])) {
    browserPausesAudioOnCloseModal = true;
  }

  // The latch indicates that playback should be restarted after the pause
  const [modalDialogClosedLatch, setModalDialogClosedLatch] =
    useState<boolean>(false);

  useEffect(() => {
    if (!isAuthorisedToPlayAudio) {
      clearAudios();
    }
  }, [isAuthorisedToPlayAudio]);

  const addAudio = (payload: AudioItem | Array<AudioItem>) => {
    dispatchPopOutStateAction({
      type: 'ADD_AUDIO',
      payload,
    });
  };

  const noActiveItems = (playlist: Playlist): boolean => {
    const active = playlist.filter(p => p.state.active).length;
    return active === 0;
  };

  const playAudio = itemId => {
    if (!itemId) {
      return;
    }
    dispatchPopOutStateAction({
      type: 'PLAY_AUDIO',
      payload: { itemId },
    });
    /**
     * in order to initiate the ability to do autoplaying of audio in safari,
     * the initial play() call need to be a direct child in the call stack of
     * a user event, This is why we need to store a reference to the audio player
     * and invoke the method directly!
     */
    if (audioPlayerInstance.current && noActiveItems(popOutState.playlist)) {
      audioPlayerInstance.current.tech().el().play();
    }
  };

  const pauseAudio = itemId => {
    dispatchPopOutStateAction({
      type: 'PAUSE_AUDIO',
      payload: {
        itemId: itemId || getCurrentlyActive()?.itemId,
      },
    });
  };

  const setAllInactive = () => {
    dispatchPopOutStateAction({
      type: 'SET_ALL_INACTIVE',
    });
    dispatchPopOutStateAction({
      type: 'SET_POP_OUT_VISIBILITY_STATE',
      payload: VisibilityStates.Hidden,
    });
  };

  const getCurrentlyActive = () => {
    const currentPlaying = popOutState.playlist.filter(
      item => item.state.active,
    )[0];
    return currentPlaying || null;
  };

  const toggleAudioState = itemId => {
    if (!itemId) {
      return;
    }
    const audioItem = getAudioFromPlaylist(popOutState.playlist, itemId);
    if (!audioItem) {
      return;
    }

    switch (audioItem.state.playState) {
      case PlayStates.Playing:
        pauseAudio(itemId);
        break;
      case PlayStates.Paused:
        playAudio(itemId);
        break;
    }
  };

  const playNextAudio = () => {
    const currentId = getCurrentlyActive()?.itemId;
    if (currentId) {
      const nextId = getNextAudio(popOutState.playlist, currentId);
      if (nextId) {
        playAudio(nextId);
      }
    }
  };

  const playPreviousAudio = () => {
    const currentId = getCurrentlyActive()?.itemId;
    if (currentId) {
      const prevId = getPreviousAudio(popOutState.playlist, currentId);
      if (prevId) {
        playAudio(prevId);
      }
    }
  };

  const setAudioPlayerInstance = player => {
    audioPlayerInstance.current = player;
  };

  const clearAudios = () => {
    dispatchPopOutStateAction({
      type: 'CLEAR_AUDIOS',
    });
    dispatchPopOutStateAction({
      type: 'SET_POP_OUT_VISIBILITY_STATE',
      payload: VisibilityStates.Hidden,
    });
  };

  return (
    <PopOutAudioContext.Provider
      value={{
        addAudio,
        getCurrentlyActive,
        playAudio,
        pauseAudio,
        toggleAudioState,
        playNextAudio,
        playPreviousAudio,
        setAllInactive,
        playlist: popOutState.playlist,
        setAudioPlayerInstance,
        clearAudios,
        audioPlayerContainerHeight,
        setAudioPlayerContainerHeight,
        audioPlayerPortalOverrideParent,
        setAudioPlayerPortalOverrideParent,
        audioPlayerPortalOverrideTrigger,
        setAudioPlayerPortalOverrideTrigger,
        modalDialogClosedLatch,
        setModalDialogClosedLatch,
        browserPausesAudioOnCloseModal,
        popOutVisibilityState: popOutState.visibilityState,
      }}
    >
      {children}
    </PopOutAudioContext.Provider>
  );
};
